import {Injectable} from '@angular/core';

import {map, switchMap} from 'rxjs/operators';

import {
	AccountSubTypeEnum,
	AccountSummary,
	CurrencyTypesEnum,
	FundPortalCashAccountInputModel, FundPortalService,
	HttpStatusCodesEnum,
	IAccountSummary,
	ReportsService,
	ServerResponse,
	TradingAccountInterface,
	UserRelationTypesEnum,
} from '@spa/api';

import {Helper} from '../../../shared/classes';
import {AlertsService, Client} from "../../../shared/services";
import {EnrollTermsAndConditionsComponentErrorStatesEnum} from "../../enroll/enums";

export type GroupedAccountsByCashAccountType = {
	[cashAccountId: string]: {
		cashAccount: AccountSummary,
		tradingAccounts: AccountSummary[]
	},
};

@Injectable()
export class TradingClientService {
	private accountsWithUserAccess: AccountSummary[];

	constructor(
		private readonly alertsService: AlertsService,
		private readonly reportsService: ReportsService,
		private readonly fundPortalService: FundPortalService
		) {
		this.accountsWithUserAccess = [];
	}

	private createCashAccount(client: Client): ServerResponse<AccountSummary[]> {
		const individualId = client.userRelationType === UserRelationTypesEnum.Individual ? client.id : null;
		const companyId = client.userRelationType === UserRelationTypesEnum.CompanyEmployee ? client.companyId : null;

		const model = new FundPortalCashAccountInputModel(
			individualId,
			CurrencyTypesEnum.Euro,
			null,
			null,
			null,
			null,
			companyId
		);

		return this.fundPortalService.registerCashAccount(model)
			.pipe(switchMap(response => {
				if (response.status !== HttpStatusCodesEnum.Ok) {
					this.alertsService.addAlert('Creating cash account failed.');
				}

				return this.getAllAccountsWithAccessAsync(client, false, false);
			}));
	}

	private mapAccounts(): void {
		if (this.accountsWithUserAccess && this.accountsWithUserAccess.length > 0) {
			const cashAccounts = this.getAllCashAccountsWithAccess();
			const tradingAccounts = this.getAllTradingAccountsWithAccess();

			cashAccounts.forEach(cashAccount => {
				cashAccount.accounts = tradingAccounts.filter(tradingAccount => tradingAccount.relatedCashAccountId === cashAccount.accountId);
			});
		}
	}

	getAllAccountsWithAccessAsync(
		client: Client,
		getCashed: boolean = true,
		createCashAccountIfNotExists: boolean = true
	): ServerResponse<AccountSummary[]> {
		if (
			this.accountsWithUserAccess &&
			this.accountsWithUserAccess.length > 0 &&
			getCashed
		) {
			return Helper.createSuccessResponseObservable<AccountSummary[]>(this.accountsWithUserAccess);
		}

		return this.reportsService.getAccountSummary(client.id, client.userRelationType).pipe(
			switchMap(response => {

				if (response.status !== HttpStatusCodesEnum.Ok) {

					return Helper.errorsHelper().createErrorResponseObservable<IAccountSummary[]>(response);
				}

				const invalidResponse = Helper.errorsHelper().checkForInvalidRespondedArray<IAccountSummary>(response);
				let noCashAccount = false;

				if (invalidResponse) {
					const cashAccount = (response.data as IAccountSummary[])?.find(a => a.is_cash_account);

					noCashAccount = cashAccount == null;

					if (createCashAccountIfNotExists && noCashAccount) {
						return this.createCashAccount(client);
					}
				}

				if (!invalidResponse) {
					const data = response.data as IAccountSummary[];

					this.accountsWithUserAccess =
						data.map(account => new AccountSummary(account));

					this.mapAccounts();
				}

				return Helper.createSuccessResponseObservable<AccountSummary[]>(this.accountsWithUserAccess);
			})
		);
	}

	refresh(client: Client): void {
		this.getAllAccountsWithAccessAsync(client, false, false).subscribe(x => true);
	}

	getAllAccountsWithAccess(): AccountSummary[] {
		return this.accountsWithUserAccess;
	}

	getAllTradingAccountsWithAccess(): AccountSummary[] {
		return this.accountsWithUserAccess.filter(account => !account.isCashAccount);
	}

	getTradingAccountForCash(): AccountSummary {
		return this.accountsWithUserAccess
			.find(account => !account.isCashAccount && account.subType === AccountSubTypeEnum.InstrumentCertificate);
	}

	getAllCashAccountsWithAccess(): AccountSummary[] {
		return this.accountsWithUserAccess.filter(account => account.isCashAccount);
	}

	getAccountById(accountId: number): AccountSummary | null {
		const filteredAccounts = this.accountsWithUserAccess.find(account => account.accountId === accountId);

		return filteredAccounts ?? null;
	}

	getAccountByReferenceId(referenceId: string): AccountSummary | null {
		const filteredAccounts = this.accountsWithUserAccess.find(account => account.referenceId === referenceId);

		return filteredAccounts ?? null;
	}

	getGroupedAccountsByCashAccount(statusFilter?: number[]): GroupedAccountsByCashAccountType {
		const accounts = this.getAllAccountsWithAccess().reduce((
			groupedAccounts: GroupedAccountsByCashAccountType,
			account: AccountSummary
		) => {
			if (account.isCashAccount) {
				if (typeof groupedAccounts[account.accountId] === 'undefined') {
					groupedAccounts[account.accountId] = {
						cashAccount: account,
						tradingAccounts: []
					};
				} else if (groupedAccounts[account.accountId].cashAccount === null) {
					groupedAccounts[account.accountId].cashAccount = account;
				}
			} else {
				if (typeof groupedAccounts[account.relatedCashAccountId] === 'undefined') {
					const filteredCashAccounts = this.getAllCashAccountsWithAccess()
						.find(cashAccount => cashAccount.accountId === account.relatedCashAccountId);

					groupedAccounts[account.relatedCashAccountId] = {
						cashAccount: filteredCashAccounts ?? null,
						tradingAccounts: [account]
					};
				} else {
					groupedAccounts[account.relatedCashAccountId].tradingAccounts.push(account);
				}
			}

			return groupedAccounts;
		}, {});

		if (typeof statusFilter === 'undefined') {
			return accounts;
		}

		Object.keys(accounts).forEach(cashAccountId => {
			if (accounts[cashAccountId].cashAccount !== null &&
				statusFilter.indexOf(accounts[cashAccountId].cashAccount.accountStatus) < 0) {
				delete accounts[cashAccountId];
			} else {
				accounts[cashAccountId].tradingAccounts =
					accounts[cashAccountId].tradingAccounts
						.filter(account => statusFilter.indexOf(account.accountStatus) >= 0);
			}
		});

		return accounts;
	}
}
